<template lang="pug">
//- Mirror the consumer's `class` onto the root `.app-dialog` ONLY (Vue 3 + inheritAttrs:false no
//- longer auto-merges class onto root like Vue 2 did) so dialog-level state classes like
//- `put-under` match the `.app-dialog.put-under` SCSS. The class is kept OFF the inner
//- `.modal-dialog` (v-bind="modalAttrs", not $attrs) so `.<consumer-class>` matches a single
//- element; descendant selectors (`.<consumer-class> ...`) still resolve via the root.
.app-dialog(:class="[$attrs.class, { opened: show, loading: loading }]")
  .modal.fade(:class="{ in: show }")
    .modal-backdrop.loader.fade(
      v-show="isDismissable"
      :class="{ in: show }"
    )
    .modal-dialog(
      v-loading="loading"
      :class="[dialogSize, position]"
      v-bind="modalAttrs"
    )
      .modal-content
        .modal-header(
          v-if="title"
          data-testid="app-dialog-header"
        )
          button.close(
            v-show="isDismissable"
            type="button"
            data-dismiss="modal"
            aria-hidden="true"
            data-testid="app-dialog-close-button"
            @click="close"
          )
            i.hi.hi-close.hi-12
          slot(name="corner")
          .d-flex
            slot(name="icon")
            h4.modal-title {{ title }}
              slot(name="titleLabel")
          .modal-subtitle(v-if="subtitle") {{ subtitle }}
          slot(name="header")
        .modal-body
          slot
        .modal-footer(v-if="hasFooter")
          slot(name="footer")
</template>

<script>
import { omit } from 'lodash-es';
import { DIALOG_OPEN_WIN_EVENT } from '~/app_constants';
const MODAL_OPEN_BODY_CLASS = 'modal-open';

export default {
  name: 'AppDialog',
  inheritAttrs: false,
  props: {
    title: {
      type: String,
    },
    subtitle: {
      type: String,
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isDismissable: {
      type: Boolean,
      default: true,
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
    },
    position: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'centered'].includes(value),
    },
  },
  computed: {
    hasFooter() {
      return this.$slots.footer;
    },
    dialogSize() {
      switch (this.size) {
        case 'small':
          return 'modal-sm';
        case 'smedium':
          return 'modal-smd';
        case 'medium':
          return 'modal-md';
        case 'large':
          return 'modal-lg';
        case 'x-large':
          return 'modal-lg modal-xl';
        case 'xx-large':
          return 'modal-lg modal-xxl';
        default:
          return '';
      }
    },
    // `$attrs` without `class`. The consumer's class is mirrored onto the root `.app-dialog`
    // (see template), so keep it OFF the inner `.modal-dialog` — otherwise `.<consumer-class>`
    // resolves to two elements. Descendant selectors still resolve via the root.
    modalAttrs() {
      return omit(this.$attrs, 'class');
    },
  },
  watch: {
    show(val) {
      if (val && this.appendToBody) {
        this.addBodyClass();
      } else {
        this.removeBodyClass();
      }

      this.dispatchWindowOpen();
    },
  },
  mounted() {
    if (this.appendToBody) {
      window.document.body.appendChild(this.$el);
    }

    if (this.appendToBody && this.show) {
      this.addBodyClass();
    }

    this.dispatchWindowOpen();

    document.addEventListener('keydown', this.handleEscapeKey);
  },
  beforeUnmount() {
    if (this.appendToBody) {
      if (this.show) {
        this.removeBodyClass();
      }

      if (this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
    }

    document.removeEventListener('keydown', this.handleEscapeKey);
  },
  methods: {
    dispatchWindowOpen() {
      if (this.show) {
        window.dispatchEvent(new CustomEvent(DIALOG_OPEN_WIN_EVENT));
      }
    },
    close() {
      this.$emit('close');
    },
    handleEscapeKey(e) {
      if (this.show === true && this.isDismissable === true && e.keyCode === 27) {
        this.close();
      }
    },
    addBodyClass() {
      window.document.body.classList.add(MODAL_OPEN_BODY_CLASS);
    },
    removeBodyClass() {
      window.document.body.classList.remove(MODAL_OPEN_BODY_CLASS);
    },
  },
};
</script>
